import { useState } from 'react';
import { occupiedDefaults } from '../../../lib/s2/defaults';
import { previewOccupied, setStanceCell } from '../../../lib/s2/occupiedPreview';
import {
  OCCUPIED_APPROACHES,
  OCCUPIED_DISPOSITIONS,
  OCCUPIED_STANCES,
  type OccupiedRulesDef,
  type StanceGrid,
} from '../../../lib/s2/types';
import { withDefaults } from '../../../lib/teamsDefaults';
import { issuesUnder, type FieldErrors } from '../../../lib/validate';
import {
  CheckboxField,
  Field,
  Fieldset,
  IssueList,
  NumberField,
  NumberInput,
  SelectInput,
  StringListEditor,
} from '../fields';
import { useContentBundle } from '../refs';
import type { FormProps } from '../types';

type Rules = Required<OccupiedRulesDef>;

/** The `occupied` block with its C# defaults, the tell roll too. */
function occupiedWithDefaults(v: OccupiedRulesDef | null | undefined): Rules {
  const d = occupiedDefaults();
  const r = withDefaults(d, (v ?? {}) as Rules);
  return { ...r, trueTells: withDefaults(d.trueTells, r.trueTells ?? d.trueTells) };
}

/**
 * The `occupied` section (occupied.html §2, §4 Admin): the rolls, the zombie multiplier, the tell roll, the stance grid editor
 * (6 dispositions × 3 approaches, each cell a small weight list over the stances) and trades; then the preview: pick a crew and a
 * disposition, see the tells and the stance odds from the real content.
 */
export function OccupiedRulesForm({ value, onChange, errors }: FormProps<OccupiedRulesDef>) {
  const r = occupiedWithDefaults(value);
  const set = <K extends keyof Rules>(k: K, x: Rules[K]) => onChange({ ...r, [k]: x });
  const num = (
    k: keyof Rules & string,
    label: string,
    o: { hint?: string; integer?: boolean; step?: number } = {},
  ) => (
    <NumberField
      label={label}
      hint={o.hint}
      integer={o.integer}
      min={0}
      step={o.step}
      value={r[k] as number}
      issues={errors[k]}
      onChange={(x) => set(k, (x ?? 0) as never)}
    />
  );
  const mult = r.zombieMult ?? {};
  return (
    <>
      <Fieldset legend="Rolls">
        <CheckboxField
          label="Enabled"
          checked={!!r.enabled}
          onChange={(x) => set('enabled', x)}
          hint="Off: no destination is occupied."
        />
        {num('chancePerDestination', 'Chance per destination', {
          step: 0.01,
          hint: 'Rolled per eligible destination when the set is generated (0..1).',
        })}
        {num('minPerSet', 'Min per set', { integer: true })}
        {num('maxPerSet', 'Max per set', { integer: true })}
        {num('minRooms', 'Min rooms', {
          integer: true,
          hint: 'The smallest building that can be occupied.',
        })}
        {num('maxHumansAlive', 'Humans alive at once', { integer: true })}
        <StringListEditor
          label="Categories"
          hint="Building categories that can be occupied (medical, food, police, supply_cache)."
          value={r.categories ?? []}
          errors={errors}
          base="categories"
          onChange={(x) => set('categories', x)}
        />
      </Fieldset>
      <Fieldset legend="Zombies inside × (per disposition)">
        {(['default', ...OCCUPIED_DISPOSITIONS] as string[]).map((k) => (
          <Field
            key={k}
            label={k}
            issues={errors[`zombieMult.${k}`]}
            hint={k === 'default' ? 'For the rest; empty = 1.' : undefined}
          >
            {(id) => (
              <NumberInput
                id={id}
                nullable
                step={0.1}
                min={0}
                value={mult[k] ?? null}
                onChange={(x) => {
                  const next = { ...mult };
                  if (x == null) delete next[k];
                  else next[k] = x;
                  set('zombieMult', next);
                }}
              />
            )}
          </Field>
        ))}
      </Fieldset>
      <Fieldset legend="Tells">
        <NumberField
          label="Two true tells chance"
          hint="Per gate: 1 true tell, 2 at this chance."
          step={0.05}
          min={0}
          max={1}
          value={r.trueTells.twoChance}
          issues={errors['trueTells.twoChance']}
          onChange={(x) => set('trueTells', { ...r.trueTells, twoChance: x ?? 0 })}
        />
        <NumberField
          label="Red herring chance"
          hint="Plus one tell pointing elsewhere, at this chance."
          step={0.05}
          min={0}
          max={1}
          value={r.trueTells.herringChance}
          issues={errors['trueTells.herringChance']}
          onChange={(x) => set('trueTells', { ...r.trueTells, herringChance: x ?? 0 })}
        />
      </Fieldset>
      <Fieldset legend="Trades, recruits, stash">
        {num('tradeOffers', 'Trade offers', { integer: true })}
        {num('fairPriceMult', 'Fair price ×', {
          step: 0.05,
          hint: 'Give value ≥ this × get value; a crew can override.',
        })}
        {num('maxTrades', 'Max trades', { integer: true })}
        {num('maxRecruits', 'Max recruits', { integer: true })}
        {num('stashRolls', 'Stash rolls', { integer: true })}
      </Fieldset>
      <StanceGridEditor
        grid={r.stances as StanceGrid}
        errors={errors}
        onChange={(g) => set('stances', g)}
      />
      <OccupiedPreviewPanel rules={r} />
    </>
  );
}

const sumOf = (w: Record<string, number> | undefined) =>
  Object.values(w ?? {}).reduce((a, x) => a + (x ?? 0), 0);

/** 6 × 3 cells; each cell is a weight list over the 7 stances (per 100). A cell whose weights don't add to 100 is marked. */
export function StanceGridEditor({
  grid,
  errors,
  onChange,
}: {
  grid: StanceGrid | undefined;
  errors: FieldErrors;
  onChange: (g: StanceGrid) => void;
}) {
  const [open, setOpen] = useState<{ d: string; a: string } | null>(null);
  const cell = open ? (grid?.[open.d]?.[open.a] ?? {}) : undefined;
  return (
    <section className="mb-4 rounded border-2 border-ink bg-white p-3" aria-label="Stance grid">
      <h3 className="text-sm font-bold">Stance grid</h3>
      <p className="text-xs text-bruise">
        Disposition × approach → stance weights (per 100). Click a cell to edit its weights.
      </p>
      <IssueList issues={issuesUnder(errors, 'stances')} />
      <table className="mt-2 w-full text-xs">
        <thead>
          <tr>
            <th className="text-left">Disposition</th>
            {OCCUPIED_APPROACHES.map((a) => (
              <th key={a} className="text-left uppercase">
                {a}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {OCCUPIED_DISPOSITIONS.map((d) => (
            <tr key={d} className="border-t border-newsprint">
              <td className="pr-2 font-semibold">{d}</td>
              {OCCUPIED_APPROACHES.map((a) => {
                const w = grid?.[d]?.[a];
                const sum = sumOf(w);
                const isOpen = open?.d === d && open?.a === a;
                return (
                  <td key={a} className="p-0.5">
                    <button
                      type="button"
                      aria-label={`Edit ${d} ${a}`}
                      onClick={() => setOpen(isOpen ? null : { d, a })}
                      className={`w-full rounded border px-1 py-0.5 text-left ${isOpen ? 'border-ink bg-benday/30' : 'border-newsprint'} ${w && sum !== 100 ? 'text-blam' : ''}`}
                    >
                      {w && Object.keys(w).length ? (
                        Object.entries(w)
                          .filter(([, x]) => x > 0)
                          .map(([k, x]) => `${k} ${x}`)
                          .join(' · ')
                      ) : (
                        <span className="text-bruise">empty</span>
                      )}
                      {w && sum !== 100 && <span> (Σ {sum})</span>}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {open && (
        <div
          className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4"
          aria-label={`Weights for ${open.d} ${open.a}`}
        >
          {OCCUPIED_STANCES.map((s) => (
            <Field key={s} label={s} issues={errors[`stances.${open.d}.${open.a}.${s}`]}>
              {(id) => (
                <NumberInput
                  id={id}
                  integer
                  min={0}
                  value={cell?.[s] ?? 0}
                  onChange={(x) =>
                    onChange(setStanceCell(grid, open.d, open.a, { ...(cell ?? {}), [s]: x ?? 0 }))
                  }
                />
              )}
            </Field>
          ))}
          <p className="col-span-full text-xs">
            Sum:{' '}
            <span className={sumOf(cell) === 100 ? '' : 'font-semibold text-blam'}>
              {sumOf(cell)}
            </span>{' '}
            / 100
          </p>
        </div>
      )}
    </section>
  );
}

const pct = (p: number) => `${Math.round(p * 100)}%`;

/** Pick a crew and a disposition; see the disposition odds, the tells per gate and the stance odds. */
function OccupiedPreviewPanel({ rules }: { rules: Rules }) {
  const b = useContentBundle();
  const crews = (b.crews ?? []).filter((c) => c?.id);
  const [crewId, setCrewId] = useState<string>(crews[0]?.id ?? '');
  const [disp, setDisp] = useState<string>('normal');
  const crew = crews.find((c) => c.id === crewId);
  const p = previewOccupied(rules, crew, b.tells, disp);
  return (
    <section
      className="mb-4 rounded border-2 border-ink bg-white p-3"
      aria-label="Occupied preview"
    >
      <h3 className="text-sm font-bold">Preview</h3>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <Field label="Crew">
          {(id) => (
            <SelectInput
              id={id}
              value={crewId}
              none="(no crew)"
              options={crews.map((c) => ({ value: c.id!, label: `${c.name || c.id} (${c.id})` }))}
              onChange={(x) => setCrewId(x ?? '')}
            />
          )}
        </Field>
        <Field label="Disposition">
          {(id) => (
            <SelectInput
              id={id}
              value={disp}
              options={OCCUPIED_DISPOSITIONS.map((d) => ({ value: d, label: d }))}
              onChange={(x) => setDisp(x ?? 'normal')}
            />
          )}
        </Field>
      </div>
      {crew && (
        <p className="mt-2 text-xs">
          <span className="font-semibold">{crew.name || crew.id}</span> is:{' '}
          {p.dispositions.length
            ? p.dispositions.map((x) => `${x.key} ${pct(x.p)}`).join(' · ')
            : 'no disposition weights'}
        </p>
      )}
      <p className="text-xs">Zombies inside × {p.zombieMult}</p>
      {p.mapTells.length > 0 && (
        <p className="text-xs">
          Map card (far tells): {p.mapTells.map((t) => t.text || t.id).join(' / ')}
        </p>
      )}
      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        {p.gates.map((g) => (
          <div key={g.gate} className="rounded border border-newsprint p-2 text-xs">
            <p className="font-semibold uppercase">{g.gate}</p>
            {g.truePool.length === 0 ? (
              <p className="text-blam">No true tell for {disp} here.</p>
            ) : (
              <>
                <p>True ({g.truePool.length > 1 ? `2 at ${pct(g.pTwo)}, else 1` : '1'}):</p>
                <ul className="list-disc pl-4">
                  {g.truePool.map((t) => (
                    <li key={t.id}>{t.text || t.id}</li>
                  ))}
                </ul>
              </>
            )}
            {g.herringPool.length > 0 && (
              <p className="mt-1 text-bruise">
                Herring at {pct(g.pHerring)}: one of {g.herringPool.length} (
                {[...new Set(g.herringPool.map((t) => t.disposition))].join(', ')})
              </p>
            )}
          </div>
        ))}
      </div>
      <table className="mt-2 w-full text-xs">
        <thead>
          <tr className="text-left uppercase">
            <th>Approach</th>
            <th>Stance odds</th>
          </tr>
        </thead>
        <tbody>
          {p.stances.map((s) => (
            <tr key={s.approach} className="border-t border-newsprint">
              <td className="pr-2 font-semibold">{s.approach}</td>
              <td>
                {s.stances.length ? (
                  s.stances.map((x) => `${x.key} ${pct(x.p)}`).join(' · ')
                ) : (
                  <span className="text-blam">empty cell</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
